import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { Users, User as UserIcon } from "lucide-react";

// Fallback: A 1x1 transparent pixel to act as a "blank" image
const BLANK_IMAGE = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

// Map your faculty names exactly as they appear in your Supabase 'faculty' column
const FACULTY_IMAGES = {
  "Rotterdam School of Management": "https://paoletexuwmeuatvbkvf.supabase.co/storage/v1/object/public/faculty%20logos/rsm_logo.jpg",
  "Erasmus School of Economics": "https://paoletexuwmeuatvbkvf.supabase.co/storage/v1/object/public/faculty%20logos/ese_logo_2024.jpg",
  "Erasmus School of Law": "https://paoletexuwmeuatvbkvf.supabase.co/storage/v1/object/public/faculty%20logos/esl%20logo.png",
  "Erasmus School of Social and Behavioural Sciences": "https://paoletexuwmeuatvbkvf.supabase.co/storage/v1/object/public/faculty%20logos/essb%20logo.png",
  "Erasmus Medical Center": "https://paoletexuwmeuatvbkvf.supabase.co/storage/v1/object/public/faculty%20logos/erasmus%20mc%20logo.png",
  "Erasmus School of History, Culture and Communication": "https://paoletexuwmeuatvbkvf.supabase.co/storage/v1/object/public/faculty%20logos/eshcc%20logo.jpg",
  "Erasmus School of Philosophy": "https://paoletexuwmeuatvbkvf.supabase.co/storage/v1/object/public/faculty%20logos/esp%20logo.jpg",
  "Erasmus School of Health and Policy Management":"https://paoletexuwmeuatvbkvf.supabase.co/storage/v1/object/public/faculty%20logos/eshpm%20logo.jpg",
};

export default function Leaderboard() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("individual");
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const fetchLeaderboardAndUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setCurrentUser(session?.user);

      const { data, error } = await supabase
        .from("profiles")
        .select("user_id, full_name, points, avatar_url, faculty")
        .order("points", { ascending: false });

      if (!error) setUsers(data);
      setLoading(false);
    };
    fetchLeaderboardAndUser();
  }, []);

  const getFacultyRankings = () => {
    const facultyTotals = users.reduce((acc, user) => {
      // If faculty is missing, we label it "Other" temporarily
      const faculty = user.faculty || "Other";
      acc[faculty] = (acc[faculty] || 0) + (user.points || 0);
      return acc;
    }, {});

    return Object.entries(facultyTotals)
      .map(([name, points]) => ({ name, points }))
      // FILTER: Remove "Other" or empty faculty names from the list
      .filter(f => f.name !== "Other" && f.name !== "" && f.name !== "null")
      .sort((a, b) => b.points - a.points);
  };

  if (loading) return <div style={{ textAlign: "center", padding: "3rem", color: "#12873B" }}>Loading...</div>;

  const displayData = view === "individual" ? users : getFacultyRankings();
  const userRankIndex = users.findIndex(u => u.user_id === currentUser?.id);
  const userProfile = users[userRankIndex];

  const styles = {
    container: {
      minHeight: "100vh",
      backgroundColor: "#E8EBE8", 
      padding: "1.5rem",
      paddingBottom: "160px", 
      fontFamily: "'Inter', sans-serif",
      color: "#2D302D"
    },
    header: {
      fontSize: "2.4rem", 
      fontWeight: "900",
      textAlign: "center",
      marginTop: "1.5rem",
      marginBottom: "1.5rem",
      letterSpacing: "-1.2px",
      background: "linear-gradient(135deg, #2D302D 0%, #12873B 100%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
    },
    toggleContainer: {
      display: "flex",
      backgroundColor: "#D9DFD9", 
      padding: "4px",
      borderRadius: "14px",
      marginBottom: "2rem",
      maxWidth: "380px",
      margin: "0 auto 2rem auto",
    },
    toggleBtn: (active) => ({
      flex: 1,
      padding: "0.8rem",
      borderRadius: "10px",
      border: "none",
      background: active ? "linear-gradient(135deg, #2D302D 0%, #12873B 100%)" : "transparent",
      color: active ? "#FFFFFF" : "#6B7280",
      fontWeight: "700",
      fontSize: "0.85rem",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
    }),
    row: (isTopThree) => ({
      display: "flex",
      alignItems: "center",
      padding: "1rem 1rem",
      borderRadius: "18px",
      marginBottom: "10px",
      backgroundColor: isTopThree ? "#FFFFFF" : "rgba(255, 255, 255, 0.4)", 
      position: "relative",
      overflow: "hidden"
    }),
    rankBand: (color) => ({
      position: "absolute",
      left: 0, top: 0, bottom: 0,
      width: "5px",
      backgroundColor: color,
    }),
    stickyBar: {
      position: "fixed",
      bottom: "85px", 
      left: "1.5rem",
      right: "1.5rem",
      background: "linear-gradient(135deg, #2D302D 0%, #12873B 100%)",
      borderRadius: "20px",
      padding: "1rem 1.5rem",
      display: "flex",
      alignItems: "center",
      justifyContent: "center", 
      gap: "20px",
      boxShadow: "0 10px 25px rgba(18, 135, 59, 0.3)",
      zIndex: 1000,
      color: "white"
    }
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.header}>Leaderboard</h1>

      <div style={styles.toggleContainer}>
        <button onClick={() => setView("individual")} style={styles.toggleBtn(view === "individual")}>
          <UserIcon size={16} /> Students
        </button>
        <button onClick={() => setView("faculty")} style={styles.toggleBtn(view === "faculty")}>
          <Users size={16} /> Faculties
        </button>
      </div>

      <div style={{ maxWidth: "480px", margin: "0 auto" }}>
        {displayData.map((item, index) => {
          const isTopThree = index < 3;
          const rankColors = ["#FFD700", "#94A3B8", "#CD7F32"];
          const currentColor = isTopThree ? rankColors[index] : null;

          const imageSrc = view === "individual" 
            ? (item.avatar_url || "https://via.placeholder.com/45")
            : (FACULTY_IMAGES[item.name] || BLANK_IMAGE);

          return (
            <div key={index} style={styles.row(isTopThree)}>
              {isTopThree && <div style={styles.rankBand(currentColor)} />}
              <div style={{ width: "35px", fontWeight: "900", color: currentColor || "#94A3B8", marginLeft: isTopThree ? "5px" : "0" }}>
                {index + 1}
              </div>
              <img 
                src={imageSrc} 
                alt="profile" 
                style={{ 
                  width: "42px", 
                  height: "42px", 
                  borderRadius: "50%", 
                  margin: "0 12px", 
                  border: isTopThree ? `2px solid ${currentColor}` : "none",
                  backgroundColor: "#FFF",
                  objectFit: "cover"
                }} 
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: "700", fontSize: "0.95rem" }}>{view === "individual" ? item.full_name : item.name}</div>
                {view === "individual" && <div style={{ fontSize: "0.68rem", color: "#6B7280", fontWeight: "600" }}>{item.faculty}</div>}
              </div>
              <div style={{ fontWeight: "900", color: "#12873B" }}>{item.points} pts</div>
            </div>
          );
        })}
      </div>

      {view === "individual" && userProfile && (
        <div style={styles.stickyBar}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ fontWeight: "900", fontSize: "1.2rem" }}>#{userRankIndex + 1}</div>
            <div style={{ height: "24px", width: "1px", backgroundColor: "rgba(255,255,255,0.3)" }} />
            <div style={{ fontWeight: "600" }}>Your Ranking</div>
          </div>
          <div style={{ fontWeight: "900", fontSize: "1.2rem" }}>{userProfile.points} <span style={{ fontSize: "0.8rem", opacity: 0.8 }}>pts</span></div>
        </div>
      )}
    </div>
  );
}